/**
 * Per-browser notification dismissals, shared by the navbar bell and the
 * dashboard's "Needs attention" card.
 *
 * Previously only the bell tracked these, so dismissing there left the card
 * showing the same item — two surfaces reading one API and disagreeing about
 * what the user had already dealt with.
 *
 * Backed by a module-level store rather than per-component state, because the
 * bell and the card are mounted **in the same document**: the `storage` event
 * only fires for *other* tabs, so two independent useState copies would drift
 * apart on the very screen where both are visible. useSyncExternalStore gives
 * every subscriber the same snapshot, and the storage listener covers the
 * cross-tab case on top.
 *
 * Dismissals are ids, not a blanket mute: the server rebuilds notification ids
 * from the underlying facts (a worsening peak, a further bump), so a situation
 * that develops resurfaces even after being dismissed, while a steady one
 * stays hidden. That property is why this stores ids rather than a "hide
 * everything before <timestamp>" watermark.
 *
 * localStorage, so it is per-browser and never leaves the device — dismissing
 * is a personal "I've seen this", not a state change other users inherit.
 */
import { useCallback, useSyncExternalStore } from "react";

const DISMISSED_KEY = "naas_dismissed_notifications";

// Ids accumulate as events come and go, and nothing prunes them server-side.
// Bounded so a long-lived browser doesn't grow the entry without limit; oldest
// dropped first, and with a handful of live notifications the cap sits far
// above anything that could resurrect a current item.
const MAX_REMEMBERED = 200;

function readStorage(): string[] {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === "string");
  } catch {
    // Corrupt or unavailable storage must not break the surfaces that read it;
    // the worst case is that a dismissal doesn't persist.
    return [];
  }
}

// Cached snapshot. useSyncExternalStore compares by reference, so this must
// only change identity when the contents actually change — returning a fresh
// array each read would re-render every subscriber on every tick.
let snapshot: string[] = readStorage();
const listeners = new Set<() => void>();

function emit(next: string[]): void {
  snapshot = next;
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(next.slice(-MAX_REMEMBERED)));
  } catch {
    /* quota or private mode — the dismissal still applies for this session */
  }
  for (const l of listeners) l();
}

function sameContents(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function subscribe(listener: () => void): () => void {
  // Re-read on the first subscriber. The snapshot is initialised at import,
  // which is too early if storage is written afterwards — by a previous
  // session restoring, or simply by the module loading before the value it
  // should reflect exists. Cheap (one localStorage read per mount cycle) and
  // it keeps a freshly-mounted surface from showing items the user already
  // dismissed.
  if (listeners.size === 0) {
    const fresh = readStorage();
    if (!sameContents(fresh, snapshot)) snapshot = fresh;
  }
  listeners.add(listener);
  // Cross-tab: another tab's write updates this one.
  const onStorage = (e: StorageEvent) => {
    if (e.key !== DISMISSED_KEY) return;
    snapshot = readStorage();
    for (const l of listeners) l();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

const getSnapshot = () => snapshot;

export function useDismissedNotifications() {
  const ids = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const dismiss = useCallback((id: string) => {
    if (snapshot.includes(id)) return;
    emit([...snapshot, id]);
  }, []);

  /** Dismiss everything currently on screen.
   *
   * Takes the visible ids rather than clearing wholesale, so it only affects
   * what the user can actually see — anything arriving afterwards still shows.
   * "Dismiss all" means "I've read these", not "stop telling me things".
   */
  const dismissAll = useCallback((visibleIds: string[]) => {
    const next = [...snapshot];
    for (const id of visibleIds) if (!next.includes(id)) next.push(id);
    if (next.length !== snapshot.length) emit(next);
  }, []);

  /** Undo a dismiss-all — the same list, put back. */
  const restore = useCallback((restoreIds: string[]) => {
    const undo = new Set(restoreIds);
    emit(snapshot.filter((id) => !undo.has(id)));
  }, []);

  const isDismissed = useCallback((id: string) => ids.includes(id), [ids]);

  return { dismiss, dismissAll, restore, isDismissed };
}

/** Test seam: reset the module store between cases. */
export function __resetDismissedForTests(): void {
  snapshot = readStorage();
  for (const l of listeners) l();
}
